import { useMemo, useRef, useState } from "react";
import { Check, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApplyLabel, useLabels, useMessageLabels } from "@/api/hooks";
import { cn } from "@/lib/utils";
import { useDismiss } from "@/lib/useDismiss";
import { useFocusTrap } from "@/lib/useFocusTrap";

export function LabelPicker({ messageId, triggerClassName }: { messageId: string; triggerClassName?: string }) {
    const labels = useLabels();
    const applied = useMessageLabels(messageId);
    const apply = useApplyLabel();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const appliedIds = useMemo(
        () => new Set((applied.data ?? []).map((l) => l.id)),
        [applied.data]
    );

    useDismiss(ref, () => setOpen(false), open);
    useFocusTrap(popoverRef, open);

    return (
        <div ref={ref} className="relative">
            <Button
                variant="ghost"
                size="icon"
                aria-label="Labels"
                aria-haspopup="menu"
                aria-expanded={open}
                title="Labels"
                className={triggerClassName}
                onClick={() => setOpen((v) => !v)}
            >
                <Tag className="h-4 w-4" />
            </Button>
            {open ? (
                <div
                    ref={popoverRef}
                    role="menu"
                    aria-label="Apply labels"
                    className="absolute right-0 top-full z-20 mt-1 w-56 rounded-md border bg-card p-1 shadow-lg"
                >
                    <header className="px-2 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Apply labels
                    </header>
                    {labels.isPending ? (
                        <p className="px-2 py-2 text-xs text-muted-foreground">Loading…</p>
                    ) : labels.data && labels.data.length > 0 ? (
                        <ul className="max-h-64 overflow-auto">
                            {labels.data.map((l) => {
                                const on = appliedIds.has(l.id);
                                return (
                                    <li key={l.id}>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                apply.mutate({
                                                    messageId,
                                                    labelId: l.id,
                                                    apply: !on
                                                })
                                            }
                                            className={cn(
                                                "flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm hover:bg-accent",
                                                on && "bg-accent/60"
                                            )}
                                        >
                                            <span
                                                className="inline-block h-2.5 w-2.5 rounded-full"
                                                style={{ backgroundColor: l.color }}
                                                aria-hidden="true"
                                            />
                                            <span className="flex-1 truncate">{l.name}</span>
                                            {on ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className="px-2 py-2 text-xs text-muted-foreground">
                            No labels yet. Create one in Settings → Labels.
                        </p>
                    )}
                </div>
            ) : null}
        </div>
    );
}

export function LabelChips({ messageId }: { messageId: string }) {
    const applied = useMessageLabels(messageId);
    if (!applied.data || applied.data.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1">
            {applied.data.map((l) => (
                <span
                    key={l.id}
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px]"
                    style={{ borderColor: l.color, color: l.color }}
                    title={`Label: ${l.name}`}
                >
                    <span
                        className="inline-block h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: l.color }}
                    />
                    {l.name}
                </span>
            ))}
        </div>
    );
}
